/**
 * 初期設定のチェックリスト（ダッシュボード上部）。
 *
 * ## なぜ別画面のウィザードにしないのか
 *
 * 申込から最初の 1 通が届くまでには「ドメインを追加 → DNS を設定 →
 * 所有権の確認（DNS の伝播待ち） → メールアドレスを作成 → 送受信の確認 →
 * 支払い方法の登録」という 6 段がある。**このうち 3 段目は数十分〜数時間
 * 待つ**ので、モーダルのウィザードにすると必ず途中で離脱し、戻る場所が
 * 分からなくなる。ホームに常設して、いつ戻ってきても続きが分かる形にした。
 *
 * ## 段の判定はサーバが持つ
 *
 * 完了条件は `GET /portal/onboarding` が返す（`apps/api/src/services/onboarding.ts`）。
 * 画面はラベルと導線だけを持つ。**判定を画面に置くと、権限が無くて読めない
 * API の分だけ段が消えたり、押すと 403 になる段が残ったりする**（実際に
 * そうなっていた）。見せられない段はサーバが最初から返さない。
 *
 * ## 消え方
 *
 * - 全段が終わったら **カードごと消える**。永久に居座るチェックリストは
 *   ダッシュボードの一等地を潰すだけになる。
 * - 終わった段は畳む（件数だけ出す）。残っている作業に目が行くようにする。
 * - 「あとで」を押すと 1 行に畳んで localStorage に覚える。**消し切らない**
 *   のは、完全に隠すと戻る導線が製品の中から消えるため。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@mail-sv/ui';
import { CheckCircle2, ChevronDown, Circle, Clock, RefreshCw } from 'lucide-react';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { AppLink } from '../lib/nav.js';
import { ErrorAlert } from './async.js';
import type { OnboardingSnapshot, OnboardingStepKey } from '../lib/api-types.js';

/** 段ごとの文言。**順序は API が返す配列に従う**（ここは辞書に徹する）。 */
const STEP_TEXT: Record<
  OnboardingStepKey,
  { title: string; description: string; action: string }
> = {
  domain: {
    title: 'ドメインを追加する',
    description: 'お持ちのドメイン（example.jp など）を登録します。',
    action: 'ドメインを追加する',
  },
  dns: {
    title: 'DNS を設定する',
    description: '表示されるレコードを、ドメインを購入した会社の管理画面に貼り付けます。',
    action: 'DNS の設定を開く',
  },
  verify: {
    title: '所有権の確認を待つ',
    description: '設定した内容がインターネット全体に行き渡ると、自動で確認が完了します。',
    action: '確認の状況を見る',
  },
  mailbox: {
    title: 'メールアドレスを作成する',
    description: 'info@ や support@ などを作ります。',
    action: 'メールアドレスを作る',
  },
  mailflow: {
    title: '送受信を確認する',
    description: '作成したアドレス宛にテストメールを送って、届くことを確かめます。',
    action: 'メールソフトの設定を見る',
  },
  billing: {
    title: 'お支払い方法を登録する',
    description: '無料期間の終了後も止まらないよう、先に登録しておくことをおすすめします。',
    action: 'お支払い方法を登録する',
  },
};

const DISMISS_KEY = 'mail-sv.portal.onboarding-collapsed';

function loadCollapsed(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    // プライベートモードでは覚えられないが、表示は続ける。
    return false;
  }
}

function saveCollapsed(collapsed: boolean): void {
  try {
    if (collapsed) window.localStorage.setItem(DISMISS_KEY, '1');
    else window.localStorage.removeItem(DISMISS_KEY);
  } catch {
    /* 覚えられないだけで動作は続ける */
  }
}

/** 反映待ちの目安を人間の言葉にする。DNS の TTL（秒）が入る。 */
function propagationHint(seconds: number): string {
  if (seconds <= 0) return '数分〜数時間';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `およそ ${minutes} 分`;
  return `およそ ${Math.round(minutes / 60)} 時間`;
}

export function OnboardingChecklist() {
  const [collapsed, setCollapsed] = React.useState(loadCollapsed);

  const onboarding = useQuery({
    queryKey: qk.onboarding(),
    queryFn: () => api.get<OnboardingSnapshot>('/onboarding'),
    /*
     * 既定（30 秒）より短くする。**段を 1 つ終えてホームに戻る**のが
     * この画面の使われ方なので、戻ってきたのにチェックが付いていないと
     * 「終わっていないのか」と不安にさせる。問い合わせ 1 本は軽い。
     */
    staleTime: 10_000,
    /*
     * 権限が無いロールでも 200（空の進捗）が返る設計なので、再試行の
     * 必要は無い。ネットワーク断で何度も叩いてダッシュボードの描画を
     * 遅らせないよう 1 回で諦める。
     */
    retry: false,
  });

  const data = onboarding.data;

  // 読み込み中に骨組みを出さない。**一瞬出て消えるカード**は、
  // ページの高さが跳ねるだけで情報量がゼロ。
  if (!data) return null;
  // 段が 1 つも無い（＝見せる相手ではない）／全部終わった。
  if (data.steps.length === 0 || data.completed) return null;

  const doneCount = data.steps.filter((s) => s.done).length;
  const total = data.steps.length;
  const next = data.steps.find((s) => !s.done);

  if (collapsed) {
    return (
      <button
        type="button"
        onClick={() => {
          setCollapsed(false);
          saveCollapsed(false);
        }}
        className="flex w-full items-center justify-between gap-3 rounded-lg border border-border bg-panel px-4 py-2.5 text-left transition-colors hover:bg-hover"
      >
        <span className="min-w-0 text-sm">
          <span className="font-medium">はじめの設定</span>
          <span className="ml-2 text-muted-foreground">
            残り {total - doneCount} 件
            {next ? ` — 次は「${STEP_TEXT[next.key].title}」` : ''}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
          開く
          <ChevronDown className="size-4" aria-hidden />
        </span>
      </button>
    );
  }

  return (
    <Card className="border-primary/40">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 pb-3">
        <div className="min-w-0 space-y-1">
          <CardTitle className="text-base">はじめの設定</CardTitle>
          {/*
           * 件数は `total` から出す。**「6 つ」と書き固定しない**（権限で
           * 見せない段があると、書いた数と並んでいる数が食い違う）。
           */}
          <p className="text-xs text-muted-foreground">
            この {total} つが終わると、メールの送受信が使えるようになります。
          </p>
        </div>
        <Badge variant="muted" size="sm">
          {doneCount} / {total} 完了
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <Progress done={doneCount} total={total} />

        {/* 終わった段は 1 行に畳む。残っている作業に目が行くようにする。 */}
        {doneCount > 0 ? (
          <DoneSummary steps={data.steps.filter((s) => s.done).map((s) => s.key)} />
        ) : null}

        <ol className="space-y-3">
          {data.steps
            .filter((s) => !s.done)
            .map((step, index) => (
              <StepRow
                key={step.key}
                stepKey={step.key}
                current={index === 0}
                snapshot={data}
              />
            ))}
        </ol>

        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
          <Button asChild variant="ghost" size="sm">
            <AppLink to="/help">それぞれの手順を詳しく見る</AppLink>
          </Button>
          {/*
           * **「あとで」。** 手順を既に理解している契約者にとって、
           * 消せないチェックリストは邪魔にしかならない。1 行に畳むだけで
           * 消し去らないのは、戻る導線を残すため。
           */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto text-muted-foreground"
            onClick={() => {
              setCollapsed(true);
              saveCollapsed(true);
            }}
          >
            あとで
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Progress({ done, total }: { done: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-full bg-border"
      role="progressbar"
      aria-valuenow={done}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-label="初期設定の進捗"
    >
      <div className="h-full rounded-full bg-success transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
}

function DoneSummary({ steps }: { steps: readonly OnboardingStepKey[] }) {
  return (
    <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
      <CheckCircle2 className="size-3.5 shrink-0 text-success" aria-hidden />
      完了:
      {steps.map((key) => (
        <span key={key} className="line-through">
          {STEP_TEXT[key].title}
        </span>
      ))}
    </p>
  );
}

/**
 * 未完了の 1 段。先頭（`current`）だけを開いて説明とボタンを出し、
 * 残りは見出しだけにする。全部を開くと「やることが 6 つある」に見えて、
 * 実際には 1 つずつしか進められないことが伝わらない。
 */
function StepRow({
  stepKey,
  current,
  snapshot,
}: {
  stepKey: OnboardingStepKey;
  current: boolean;
  snapshot: OnboardingSnapshot;
}) {
  const text = STEP_TEXT[stepKey];
  const domain = snapshot.domain;

  const to =
    stepKey === 'domain'
      ? '/domains'
      : stepKey === 'dns' || stepKey === 'verify'
        ? domain
          ? `/domains/${domain.id}/dns`
          : '/domains'
        : stepKey === 'mailbox'
          ? '/mailboxes'
          : stepKey === 'mailflow'
            ? '/mail-clients'
            : '/billing';

  return (
    <li className="flex gap-2.5">
      <Circle
        className={current ? 'mt-0.5 size-4 shrink-0 text-primary' : 'mt-0.5 size-4 shrink-0 text-muted-foreground'}
        aria-hidden
      />
      <div className="min-w-0 flex-1 space-y-2">
        <p className={current ? 'text-sm font-medium' : 'text-sm text-muted-foreground'}>
          {text.title}
        </p>
        {current ? (
          <>
            <p className="text-xs text-muted-foreground">{text.description}</p>
            {stepKey === 'verify' && domain ? <VerifyHelp domain={domain} /> : null}
            <Button asChild size="sm">
              <AppLink to={to}>{text.action}</AppLink>
            </Button>
          </>
        ) : null}
      </div>
    </li>
  );
}

/**
 * 所有権の確認待ちの案内。**ここが最も詰まる段**なので、
 *
 *   - 何本のうち何本が通ったか（進んでいることが分かる）
 *   - あとどれくらい待てばよいか（TTL から出した目安）
 *   - 待たずに今すぐ確かめるボタン
 *
 * の 3 つを必ず出す。「しばらくお待ちください」だけだと、契約者は
 * 1 分後に「壊れている」と判断してサポートへ連絡する。
 */
function VerifyHelp({ domain }: { domain: NonNullable<OnboardingSnapshot['domain']> }) {
  const queryClient = useQueryClient();
  const [checking, setChecking] = React.useState(false);

  const verify = useMutation({
    mutationFn: () =>
      api.post<{ accepted: boolean; status: string }>(
        `/domains/${encodeURIComponent(domain.id)}/verify`,
      ),
    onSuccess: () => {
      setChecking(true);
      void queryClient.invalidateQueries({ queryKey: qk.domains() });
    },
  });

  // 受理は 202 で、判定は DNS の伝播待ちの後に非同期で変わる。
  // 60 秒だけ追いかけて、それ以上は次の描画に任せる（無限には回さない）。
  React.useEffect(() => {
    if (!checking) return undefined;
    const timer = setTimeout(() => {
      setChecking(false);
      void queryClient.invalidateQueries({ queryKey: qk.onboarding() });
    }, 60_000);
    return () => clearTimeout(timer);
  }, [checking, queryClient]);

  return (
    <div className="space-y-2 rounded-lg border border-border bg-panel p-3">
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <Clock className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="font-medium">{domain.name}</span>
        <span className="text-muted-foreground">
          必須レコード {domain.verifiedRecords} / {domain.requiredRecords} 件が確認できました
        </span>
      </p>
      <p className="text-xs text-muted-foreground">
        DNS の変更が行き渡るまで {propagationHint(domain.propagationSeconds)} かかります
        （長いときは 24 時間ほど）。この画面を開いたままにしておく必要はありません。
      </p>
      {domain.lastVerificationError ? (
        <Alert variant="warning">
          <AlertDescription className="text-xs">{domain.lastVerificationError}</AlertDescription>
        </Alert>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => verify.mutate()}
          loading={verify.isPending || checking}
        >
          <RefreshCw aria-hidden />
          今すぐ確認する
        </Button>
        {checking ? <span className="text-xs text-muted-foreground">確認中です…</span> : null}
      </div>
      {verify.isError ? <ErrorAlert error={verify.error} /> : null}
    </div>
  );
}
